/**
 * Diagnostics: the event log, a deterministic RNG, and a movement recorder.
 *
 * ## Why this exists
 *
 * The recurring cost in this project is not writing features, it is answering
 * "what actually happened?" A playtest report says "Tavi keeps dying". The
 * engine can tell you he is dead. It cannot tell you whether he fell in a pit,
 * touched a rivet, ran into spikes, or ran out of clock, because every one of
 * those paths calls the same `Level.kill()` with no argument. So the question
 * gets answered by writing a throwaway script that re-simulates the level and
 * hopes to catch the frame — which is slow, is not the game, and silently
 * measures a re-implementation instead of the shipping code.
 *
 * The fix is to make the game *say* what happened, at the moment it happens, in
 * a machine-readable form. Everything here exists to make that cheap.
 *
 * Three pieces, deliberately independent and all pure:
 *
 * - **`EventLog`** — a ring buffer of typed events with a resolved `cause`.
 *   The death cause is the headline: `pit`, `enemy:rivet`, `lethal:spike`,
 *   `timeout`, `debug`. One call answers the question that currently needs a
 *   research project.
 * - **`Rng`** — a seeded, reproducible generator. Useless until something
 *   consumes randomness, and deliberately the standard 32-bit integer mix so
 *   the same seed gives the same stream in any language. This is what makes a
 *   bug report reproducible instead of a story.
 * - **`Recorder`** — per-frame player state plus input, so a run can be
 *   replayed, diffed, or asserted on. Saved recordings turn "it did this once"
 *   into a regression test.
 *
 * ## Why pure
 *
 * No DOM, no canvas, no module-level mutable state except through explicit
 * instances. That is what lets these be unit tested, driven from a node script,
 * and reasoned about without a browser — which is the whole reason the
 * throwaway scripts kept being rewritten.
 */

import { DT } from '../engine/constants'

// ── event log ─────────────────────────────────────────────────────────────

/**
 * What can happen.
 *
 * A closed set on purpose. An open string field invites `death2`, `died`, and
 * `player-death` in the same log, and then nothing can be filtered.
 */
export type EventKind =
  | 'death'
  | 'damage'
  | 'power'
  | 'coin'
  | 'score'
  | 'stomp'
  | 'kill'
  | 'block'
  | 'spring'
  | 'checkpoint'
  | 'respawn'
  | 'win'
  | 'note'

/**
 * Why something happened, machine-readable.
 *
 * `death` is the one that matters. These are the exhaustive set of ways the
 * player can lose a life, and they are deliberately distinct strings rather
 * than a boolean, because "he died" and "he died of a rivet" call for
 * completely different investigations.
 */
export type DeathCause =
  | 'pit' // fell out of the bottom of the course
  | 'enemy' // touched a live enemy without stomping it
  | 'lethal-object' // spikes, lava, or any placed lethal
  | 'timeout' // the clock ran out
  | 'debug' // `__debug.kill()`
  | 'unknown' // a path nobody classified; a bug in the classifier

export interface GameEvent {
  /** Simulation tick, so events can be ordered against a recording. */
  tick: number
  kind: EventKind
  /**
   * Resolved cause. Always set for `death`; optional otherwise, because
   * "score" and "checkpoint" have no cause worth recording.
   */
  cause?: DeathCause | string
  /** What killed it, for `death` from an enemy or an object. */
  source?: string
  /** Human-readable one-liner, already resolved, safe to paste in a report. */
  detail: string
  /** Tile position at the time, when it is meaningful. */
  tile?: [number, number]
  /** Anything else worth keeping. Small on purpose. */
  data?: Record<string, unknown>
}

/**
 * A bounded, ordered log of gameplay events.
 *
 * Bounded because a long session would otherwise grow without limit and take
 * the tab down with it. The default holds a long play session several times
 * over, and the oldest events are dropped rather than the newest, because when
 * you are reading a log you are almost always reading it backwards from
 * something that just went wrong.
 */
export class EventLog {
  private events: GameEvent[] = []
  private droppedCount = 0

  constructor(readonly capacity = 2000) {}

  /** Record an event. */
  emit(event: GameEvent): GameEvent {
    this.events.push(event)
    if (this.events.length > this.capacity) {
      const excess = this.events.length - this.capacity
      this.events.splice(0, excess)
      this.droppedCount += excess
    }
    return event
  }

  /**
   * Record a death, filling in the human-readable detail from the cause.
   *
   * This is the method that exists to make "why did he die" a one-liner, so it
   * takes the cause as a required argument rather than letting it be forgotten.
   */
  death(
    tick: number,
    cause: DeathCause,
    opts: Partial<GameEvent> = {},
  ): GameEvent {
    const source = opts.source ?? ''
    const detail = describeDeath(cause, source)
    return this.emit({
      tick,
      kind: 'death',
      cause,
      source: source === '' ? undefined : source,
      detail,
      tile: opts.tile,
      data: opts.data,
    })
  }

  /** Everything currently held, oldest first. */
  all(): readonly GameEvent[] {
    return this.events
  }

  /** The most recent `n` events, oldest first. */
  last(n: number): GameEvent[] {
    return this.events.slice(Math.max(0, this.events.length - n))
  }

  ofKind(kind: EventKind): GameEvent[] {
    return this.events.filter((e) => e.kind === kind)
  }

  /** Every death, in order. The first thing to look at. */
  deaths(): GameEvent[] {
    return this.ofKind('death')
  }

  /** The most recent death's cause, or null. For a one-line status readout. */
  lastDeathCause(): DeathCause | 'none' {
    const d = this.deaths()
    const last = d[d.length - 1]
    return last === undefined
      ? 'none'
      : ((last.cause as DeathCause) ?? 'unknown')
  }

  clear(): void {
    this.events = []
    this.droppedCount = 0
  }

  /** Counts per kind, plus per death cause. For "what keeps happening". */
  summary(): Record<string, unknown> {
    const byKind: Record<string, number> = {}
    const byCause: Record<string, number> = {}
    for (const e of this.events) {
      byKind[e.kind] = (byKind[e.kind] ?? 0) + 1
      if (e.kind === 'death' && typeof e.cause === 'string') {
        byCause[e.cause] = (byCause[e.cause] ?? 0) + 1
      }
    }
    return {
      held: this.events.length,
      dropped: this.droppedCount,
      byKind,
      byCause,
      lastDeath: this.lastDeathCause(),
    }
  }

  /**
   * One line per event, ready to paste into a bug report.
   *
   * The tile is included because "he died near the third pipe" is not a bug
   * report but "(118,22)" is.
   */
  toText(limit = 40): string {
    const rows = this.last(limit).map((e) => {
      const at = e.tile === undefined ? '' : ` @${e.tile[0]},${e.tile[1]}`
      return `${String(e.tick).padStart(6)}  ${e.kind.padEnd(10)} ${e.detail}${at}`
    })
    const head =
      this.droppedCount > 0
        ? `(${this.droppedCount} earlier events dropped; capacity ${this.capacity})`
        : ''
    return [...(head === '' ? [] : [head]), ...rows].join('\n')
  }
}

/** Turn a cause into something worth reading. Kept beside the type on purpose. */
export function describeDeath(cause: DeathCause, source = ''): string {
  const who = source === '' ? '' : ` (${source})`
  switch (cause) {
    case 'pit':
      return 'fell out of the bottom of the course'
    case 'enemy':
      return `killed by an enemy${who}`
    case 'lethal-object':
      return `killed by a hazard${who}`
    case 'timeout':
      return 'ran out of time'
    case 'debug':
      return 'killed from the debug console'
    case 'unknown':
      return 'died, and nothing claimed it — that is a bug in the death path'
  }
}

/** What touched the player on the frame they died. */
export type DeathContact =
  { kind: 'enemy'; id: string } | { kind: 'object'; id: string } | null

/** Everything the classifier is allowed to look at. */
export interface DeathContext {
  /** The player is below the floor of the course. */
  belowWorld: boolean
  /** The clock expired on this step. */
  timedOut: boolean
  /** The lethal thing that overlapped the player, if the shell saw one. */
  contact: DeathContact
  /** The player is flagged dead, by whatever route. */
  playerDead: boolean
}

/**
 * Work out why the player died.
 *
 * ## Why this is a pure function and not a parameter
 *
 * Every death in this game funnels through one argument-less `Level.kill()`.
 * That is the right design for the game — `Level` has no idea whether it is
 * being played by a person or a test — but it means the cause is destroyed at
 * the exact moment it becomes interesting. The shell is the only place that
 * can see all the routes, so the shell collects the evidence and hands it here,
 * and the decision itself is a pure function that can be tested directly.
 *
 * Precedence is deliberate and is the interesting part:
 *
 *  1. **Contact beats everything.** If something touched the player on the
 *     frame they died, that is the cause, even if they were also falling. A
 *     rivet that knocked Tavi into a pit killed him; "pit" would be true and
 *     useless.
 *  2. **Then the pit**, then the clock.
 *  3. **`unknown` is a real answer.** A death nothing can explain is a bug in
 *     a death path, and it should be visible as such rather than being quietly
 *     filed under the most likely-looking cause. Silently guessing here is how
 *     a real bug survives for months: the log looks plausible, so nobody looks
 *     again.
 */
export function classifyDeath(ctx: DeathContext): DeathCause {
  if (ctx.contact !== null) {
    return ctx.contact.kind === 'enemy' ? 'enemy' : 'lethal-object'
  }
  if (ctx.belowWorld) return 'pit'
  if (ctx.timedOut) return 'timeout'
  if (ctx.playerDead) return 'unknown'
  // Not dead at all. Reported as 'unknown' rather than throwing, because the
  // caller logs unconditionally and a diagnostic must not be the thing that
  // breaks.
  return 'unknown'
}

/** The registry id that should be reported as the source, if any. */
export function contactSource(contact: DeathContact): string {
  return contact === null ? '' : contact.id
}

// ── deterministic randomness ───────────────────────────────────────────────

/**
 * A seeded 32-bit PRNG.
 *
 * xorshift32: three shifts, an xor and a multiply. Chosen because it is the
 * variant most likely to be reimplemented identically in another language,
 * which is the only property that matters if a bug report from a player has to
 * be reproducible on a different machine.
 *
 * The seed is a `uint32`. `state` is readable and writable, so a run can be
 * snapshotted and restored mid-stream.
 */
export class Rng {
  private s: number

  constructor(seed: number) {
    // Zero is a fixed point of xorshift and would emit an all-zero stream, so
    // it is remapped. Passing 0 must not be a silent trap.
    this.s = Math.trunc(seed) >>> 0 || 0x9e3779b9
  }

  /** Current internal state, for snapshotting. */
  get state(): number {
    return this.s >>> 0
  }

  set state(v: number) {
    this.s = v >>> 0 || 0x9e3779b9
  }

  /** Next value in [0, 1). */
  next(): number {
    let x = this.s
    x ^= x << 13
    x >>>= 0
    x ^= x >>> 17
    x ^= x << 5
    x >>>= 0
    this.s = x
    return x / 0x100000000
  }

  /** Next integer in [0, n). Returns 0 for n <= 0 rather than throwing. */
  int(n: number): number {
    if (!Number.isFinite(n) || n <= 0) return 0
    return Math.floor(this.next() * Math.floor(n))
  }

  /**
   * A seed derived from a word, so a bug report can say `seed: "spike-gauntlet"`
   * and mean it. FNV-1a, because it is short and has good avalanche for short
   * strings.
   */
  static fromString(text: string): Rng {
    let h = 0x811c9dc5
    for (let i = 0; i < text.length; i++) {
      h ^= text.charCodeAt(i)
      h = Math.imul(h, 0x01000193) >>> 0
    }
    return new Rng(h)
  }
}

// ── movement recorder ──────────────────────────────────────────────────────

/** One simulated frame of the player, with the input that produced it. */
export interface RecordedFrame {
  tick: number
  x: number
  y: number
  vx: number
  vy: number
  onGround: boolean
  power: string
  /** Button state for that frame, so the run can be replayed exactly. */
  input: {
    left: boolean
    right: boolean
    up: boolean
    down: boolean
    jump: boolean
    run: boolean
  }
}

/** Aggregate numbers about a run, for a quick "was that normal?" read. */
export interface RecordingStats {
  frames: number
  seconds: number
  startTile: [number, number]
  endTile: [number, number]
  /** Furthest right (or left) tile reached. */
  furthestTile: number
  distancePx: number
  topSpeedPxPerFrame: number
  jumps: number
  deaths: number
}

/**
 * Per-frame capture of the player.
 *
 * Records *inputs as well as outputs*, which is the difference between a
 * recording and a log of numbers. With the input stream captured, a run can be
 * replayed through the real physics and asserted to produce the same result,
 * which turns an intermittent bug into a failing test.
 */
export class Recorder {
  private frames: RecordedFrame[] = []
  private recording = false
  private deaths = 0
  private jumps = 0
  private prevOnGround = false
  private furthest = 0
  private topSpeed = 0

  constructor(readonly capacity = 60 * 60 * 10) {}

  get isRecording(): boolean {
    return this.recording
  }

  /** Begin capturing. Does not clear what is already held. */
  start(): void {
    this.recording = true
  }

  /**
   * Stop capturing and report how it went.
   *
   * An empty recording is reported as such rather than returned as `0` frames,
   * because "you called stop without start" and "the player never moved" are
   * very different bugs and both look like length zero from the outside.
   */
  stop(): { frames: number; empty: boolean } {
    this.recording = false
    return { frames: this.frames.length, empty: this.frames.length === 0 }
  }

  clear(): void {
    this.frames = []
    this.deaths = 0
    this.jumps = 0
    this.prevOnGround = false
    this.furthest = 0
    this.topSpeed = 0
  }

  /** Call once per simulated frame, after the player has been stepped. */
  capture(player: RecordedFrame & { input: RecordedFrame['input'] }): void {
    if (!this.recording) return
    const tileX = Math.floor(player.x / 16)
    if (this.frames.length === 0) this.furthest = tileX
    if (tileX > this.furthest) this.furthest = tileX
    const speed = Math.abs(player.vx)
    if (speed > this.topSpeed) this.topSpeed = speed

    // A jump is the rising edge of "was on the ground, now is not" while
    // actually ascending. Walking off a ledge is not a jump, and counting it as
    // one would make the stat lie in exactly the courses with the most pits.
    if (
      this.frames.length > 0 &&
      this.prevOnGround &&
      !player.onGround &&
      player.vy < 0
    ) {
      this.jumps++
    }
    this.prevOnGround = player.onGround

    this.frames.push({
      tick: player.tick,
      x: player.x,
      y: player.y,
      vx: player.vx,
      vy: player.vy,
      onGround: player.onGround,
      power: player.power,
      input: { ...player.input },
    })
    if (this.frames.length > this.capacity) this.frames.shift()
  }

  /** Note a death, so `stats()` can report it. The log records the cause. */
  noteDeath(): void {
    this.deaths++
  }

  all(): readonly RecordedFrame[] {
    return this.frames
  }

  /** Aggregate numbers. Cheap, and answers "was that run normal". */
  stats(): RecordingStats {
    const first = this.frames[0]
    const last = this.frames[this.frames.length - 1]
    return {
      frames: this.frames.length,
      seconds: +(this.frames.length * DT).toFixed(2),
      startTile: first === undefined ? [0, 0] : tileOf(first),
      endTile: last === undefined ? [0, 0] : tileOf(last),
      furthestTile: this.furthest,
      distancePx:
        last === undefined || first === undefined ? 0 : last.x - first.x,
      topSpeedPxPerFrame: +this.topSpeed.toFixed(3),
      jumps: this.jumps,
      deaths: this.deaths,
    }
  }

  /**
   * Read a recording back, dropping malformed frames.
   *
   * A recording is a debugging artefact that may have crossed a storage
   * boundary, so it is treated as untrusted input: bad frames are skipped and
   * reported in the count, and a wholly corrupt payload yields an empty
   * recording instead of an exception.
   */
  static fromJSON(text: string): { frames: RecordedFrame[]; skipped: number } {
    const frames: RecordedFrame[] = []
    let skipped = 0
    try {
      const parsed: unknown = JSON.parse(text)
      const raw = (parsed as { frames?: unknown } | null)?.frames
      if (Array.isArray(raw)) {
        for (const f of raw) {
          if (isRecordedFrame(f)) frames.push(f)
          else skipped++
        }
      }
    } catch {
      return { frames, skipped: -1 }
    }
    return { frames, skipped }
  }

  /**
   * Load from `localStorage` if it is there.
   *
   * Every storage access is guarded. Storage throws in private-mode Safari and
   * when a quota is hit, and a diagnostic that crashes the game when the disk
   * is full is exactly the wrong behaviour.
   */
  static loadFrom(storage: Storage | undefined, key: string): RecordedFrame[] {
    if (storage === undefined) return []
    try {
      const text = storage.getItem(key)
      return text === null ? [] : Recorder.fromJSON(text).frames
    } catch {
      return []
    }
  }

  /** Persist, reporting whether it worked so the UI can say "session only". */
  static saveTo(
    storage: Storage | undefined,
    key: string,
    frames: RecordedFrame[],
  ): boolean {
    if (storage === undefined) return false
    try {
      storage.setItem(key, JSON.stringify({ v: 1, frames }))
      return true
    } catch {
      return false
    }
  }
}

function tileOf(f: RecordedFrame): [number, number] {
  return [Math.floor(f.x / 16), Math.floor(f.y / 16)]
}

function isRecordedFrame(v: unknown): v is RecordedFrame {
  if (typeof v !== 'object' || v === null) return false
  const f = v as Record<string, unknown>
  if (
    typeof f.tick !== 'number' ||
    typeof f.x !== 'number' ||
    typeof f.y !== 'number'
  ) {
    return false
  }
  const input = f.input
  return (
    typeof input === 'object' &&
    input !== null &&
    typeof (input as { left?: unknown }).left === 'boolean'
  )
}
